import { TeammateRoleTypes } from "@repo/app-types/entities/teammatePermissions";
import { type FC, useEffect } from "react";
import { Outlet, useMatch, useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../app/appPaths.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { useAppSelector } from "../hooks/hook.js";

export const TrainingTeamPage: FC = () => {
  const navigate = useNavigate();

  const { playbookId } = useParams();
  if (!playbookId) {
    throw new Error("Missing playbookId param.");
  }

  const currentTeammate = useAppSelector(selectTeammate);
  const isCallsPage =
    useMatch({ path: appPaths.playbookCalls.pattern, end: false }) !== null;
  const isTeamPage = useMatch(appPaths.playbookTeammates.pattern) !== null;
  const isGroupsPage = useMatch(appPaths.playbookGroups.pattern) !== null;
  const isBoundedTableRoute = isCallsPage || isTeamPage || isGroupsPage;

  // Redirect team members (non admins) to their own page since they don't have permission to view the team page.
  // We may replace this with a dedicated teammate-specific view in the future.
  useEffect(() => {
    if (
      currentTeammate?.permissions.role.type === TeammateRoleTypes.TEAM_MEMBER
    ) {
      navigate(
        appPaths.teammate.build({
          playbookId,
          teammateId: currentTeammate.id,
        }),
        {
          replace: true,
        },
      );
    }
  }, [playbookId, currentTeammate, navigate]);

  return (
    <div
      className={twMerge(
        "relative flex h-full min-h-0 flex-col",
        // A table's full scrollable overflow still contributes to this route's
        // scroll area even when its card owns the visible scrolling. Clip only
        // the bounded table routes so wheel events cannot move the whole page;
        // other playbook routes keep their normal page scrolling.
        isBoundedTableRoute ? "overflow-clip" : "overflow-y-auto",
      )}
    >
      <Outlet key={playbookId} />
    </div>
  );
};

// Playbook and teammate tabs are defined in features/teammatePerformance
